import { useState } from "react";
import { Bell, Clock, Search, HelpCircle } from "lucide-react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";

const titleMap: Record<string, { title: string; subtitle: string }> = {
  "/": { title: "Dashboard", subtitle: "Workforce overview & operational signals" },
  "/attendance": { title: "Attendance Monitor", subtitle: "Live attendance, GPS validation & exceptions" },
  "/employees": { title: "Employees", subtitle: "Manage roles, sites and shift assignments" },
  "/users": { title: "Users & Roles", subtitle: "Manage administrators and staff access" },
  "/requests": { title: "Correction Requests", subtitle: "Review and approve attendance regularizations" },
  "/sites": { title: "Sites & Geofences", subtitle: "Configure work locations and validation rules" },
  "/reports": { title: "Reports", subtitle: "Operational analytics for HR and management" },
  "/assistant": { title: "AI Assistant", subtitle: "Smart admin helper for workforce queries" },
  "/notifications": { title: "Notifications", subtitle: "Alerts, approvals and system events" },
  "/settings": { title: "Settings", subtitle: "Attendance rules, geofencing & governance" },
  "/profile": { title: "Profile", subtitle: "Your administrator account" },
};

export function AppHeader() {
  const { user, employee, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const meta = titleMap[location.pathname] ?? { title: "GeoKerja", subtitle: "Workforce operations" };
  const unread = useUnreadNotifications();
  const [query, setQuery] = useState("");

  // Global search jumps to the Employees list (the most-searched entity) with
  // the term applied. Employees reads `?q=` to seed its filter.
  const runSearch = () => {
    const q = query.trim();
    navigate(q ? `/employees?q=${encodeURIComponent(q)}` : "/employees");
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-card/80 px-4 backdrop-blur-md md:px-6">
      <SidebarTrigger className="text-muted-foreground hover:text-foreground" />

      {/* Title stays visible on mobile — hiding it left the user with no
          indication of which page they were on. Only the subtitle is dropped
          on narrow screens, where horizontal room is the constraint. */}
      <div className="min-w-0 flex-shrink">
        <h1 className="font-display truncate text-sm font-semibold leading-tight tracking-tight sm:text-base">{meta.title}</h1>
        <p className="hidden truncate text-xs text-muted-foreground md:block">{meta.subtitle}</p>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <div className="relative hidden lg:block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            placeholder="Search employees…"
            className="h-9 w-72 pl-9 bg-secondary/50 border-transparent focus-visible:bg-card"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground">↵</kbd>
        </div>

        {/* An admin who is also staff lands here, not in the employee lane, so
            this is how they reach their own check-in and My team. The
            employee header has the link back ("Admin"). */}
        {employee && (
          <Button variant="ghost" size="sm" aria-label="Employee portal" onClick={() => navigate("/me")}>
            <Clock className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Employee portal</span>
          </Button>
        )}

        <Button size="icon" variant="ghost" className="relative" onClick={() => navigate("/notifications")}>
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>

        <Button size="icon" variant="ghost" onClick={() => navigate("/assistant")}>
          <HelpCircle className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-full border bg-card p-1 pr-3 hover:bg-secondary transition-colors">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-primary text-[10px] font-semibold text-white">
                {user?.avatarInitials ?? "AD"}
              </div>
              <span className="hidden md:inline text-xs font-medium">{user?.name?.split(" ")[0] ?? "Admin"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")}>Profile</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")}>Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/assistant")}>AI Assistant</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => { logout(); navigate("/login"); }}>
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
